import { FC, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { TrackingApi, UserDictionaryApi } from 'src/api';
import { getActiveWordlistId } from 'src/utils/wordlists/getActiveWordlistId';
import { PronunciationMode } from 'src/api/userDictionary/types';
import { useBuddySessionId } from 'src/hooks/useBuddySessionId';
import { useCurrentLanguagePair } from 'src/hooks/useCurrentLanguagePair';
import { useUserContext } from 'src/context/user';
import { LearnablePronunciation } from 'src/apps/Buddy/PronunciationBuddy/LearnablePronunciation';
import { useMicrophonePermissions } from 'src/apps/Buddy/PronunciationBuddy/hooks/useMicrophonePermissions';
import { MicPermissionsView } from 'src/apps/Buddy/PronunciationBuddy/PronunciationViews';
import { PronunciationTokenUpsell } from 'src/apps/Monetisation/BlobExperienceUpsell';
import { PronunciationLimitUpsell } from 'src/apps/Monetisation/PronunciationLimitUpsell';
import { ONE_SECOND } from 'src/constants/times.const';
import { PRESENTATION_PRONUNCIATION_DAILY_FREE_TOTAL } from './presentationPronunciationAttempts';
import { usePresentationPronunciationAttempts } from './usePresentationPronunciationAttempts';
import * as S from './styles';

/** Matches the result pause in LearnablePronunciation before a retry returns to the mic. */
export const PRESENTATION_PRONUNCIATION_PASS_DISMISS_MS = 3 * ONE_SECOND;

// Lasts for the page visit: survives client-side route changes until a full reload,
// and is not scoped per user.
const passedPresentationPronunciations = new Set<string>();

const passedPronunciationKey = (languagePairId: number, learnableId: number) =>
  `${languagePairId}:${learnableId}`;

export const clearPassedPresentationPronunciations = () => {
  passedPresentationPronunciations.clear();
};

const msgs = defineMessages({
  promptTitle: {
    id: 'learning_session_presentation_pronunciation_prompt_title',
    defaultMessage: 'Say it yourself',
  },
  promptDescription: {
    id: 'learning_session_presentation_pronunciation_prompt_description',
    defaultMessage: 'Tap the mic and give it a go.',
  },
  attemptsLeft: {
    id: 'learning_session_presentation_pronunciation_attempts_left',
    defaultMessage:
      '{count, plural, =0 {No attempts left today} one {# attempt left today} few {# attempts left today} many {# attempts left today} other {# attempts left today}}',
  },
  dailyLimitBanner: {
    id: 'learning_session_presentation_pronunciation_daily_limit_banner',
    defaultMessage: "You've used your {count} free tries for today",
  },
  close: { id: 'close' },
  micPermissionTitle: { id: 'pronunciationBuddy_microphoneAccess_title' },
});

export const PronunciationPractice: FC<{
  learnableId?: number;
  languagePairId?: number;
  hasVideo?: boolean;
  /** Dark side-panel styling for embedding under CTAs on video backgrounds. */
  overlay?: boolean;
  /** Compact yellow pill prompt used by the blob experience. */
  pill?: boolean;
  trackingData?: Record<string, string>;
}> = ({
  learnableId,
  languagePairId,
  hasVideo = false,
  overlay = false,
  pill = false,
  trackingData,
}) => {
  const intl = useIntl();
  const { user } = useUserContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHoldingPassResult, setIsHoldingPassResult] = useState(false);
  const [upsellTrigger, setUpsellTrigger] = useState<'last_attempt' | 'prompt_tap' | null>(null);
  const hasPassedRef = useRef(false);
  const passTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { sessionId } = useBuddySessionId();
  const { micPermissionState, handleGiveMicPermissions } = useMicrophonePermissions();
  const { remaining, isLoading, isLocked, isRationed, consumeAttempt } =
    usePresentationPronunciationAttempts();

  const { languagePair } = useCurrentLanguagePair();
  const supportsPronunciation = languagePair?.supportsPronunciation;

  const passedKey =
    languagePairId != null && learnableId != null
      ? passedPronunciationKey(languagePairId, learnableId)
      : null;
  const hasPassedLearnable = passedKey != null && passedPresentationPronunciations.has(passedKey);

  useEffect(() => {
    setIsExpanded(false);
    setIsHoldingPassResult(false);
    hasPassedRef.current = false;
    return () => {
      if (passTimeoutRef.current != null) {
        clearTimeout(passTimeoutRef.current);
        passTimeoutRef.current = null;
      }
    };
  }, [learnableId, languagePairId]);

  const { data: pronunciationData } = useQuery({
    queryKey: [
      'presentation_pronunciation_data',
      languagePairId,
      learnableId,
      PronunciationMode.LEARN_SESSION,
      sessionId,
    ],
    queryFn: () =>
      UserDictionaryApi.getLearnablesPronunciationData(
        languagePairId as number,
        [learnableId as number],
        PronunciationMode.LEARN_SESSION,
        sessionId,
      ),
    // Wait for confirmed support (not just "not known to be unsupported") so we
    // never fire this request for a language pair that doesn't support it.
    enabled: !!languagePairId && !!learnableId && supportsPronunciation === true,
  });

  const learnable = pronunciationData?.find(data => data.learnableIdKey === learnableId);
  const attemptsLeftLabel = isRationed
    ? intl.formatMessage(msgs.attemptsLeft, { count: remaining })
    : undefined;

  if (supportsPronunciation === false || !learnable || learnable.audios.length === 0) {
    return null;
  }

  if (isRationed && isLoading) {
    return null;
  }

  const handleExpand = () => {
    const isProUser = !!user?.isPro;
    const wordlistId = getActiveWordlistId();
    void TrackingApi.trackUserAction(
      'try_pronouncing_myself',
      'learning_session',
      languagePairId ?? null,
      isProUser,
      {
        ...trackingData,
        is_pro_user: String(isProUser),
        ...(wordlistId && { wordlist_id: wordlistId }),
      },
    );

    if (isLocked) {
      setUpsellTrigger('prompt_tap');
      return;
    }

    setIsExpanded(true);
  };

  const finishPass = () => {
    if (passTimeoutRef.current != null) {
      clearTimeout(passTimeoutRef.current);
      passTimeoutRef.current = null;
    }
    setIsHoldingPassResult(false);
    setIsExpanded(false);
  };

  const handleCollapse = () => {
    if (hasPassedRef.current) {
      finishPass();
      return;
    }
    setIsExpanded(false);
  };

  const handleCorrectPronunciation = () => {
    hasPassedRef.current = true;
    if (passedKey) {
      passedPresentationPronunciations.add(passedKey);
    }
    const lastAttemptSpent = consumeAttempt() === 0;
    if (lastAttemptSpent) {
      setUpsellTrigger('last_attempt');
    }
    setIsHoldingPassResult(true);
    if (passTimeoutRef.current != null) {
      clearTimeout(passTimeoutRef.current);
      passTimeoutRef.current = null;
    }
    // Leave the expanded practice mounted under the upsell. Auto-dismiss would
    // move the modal in the tree and track screen_viewed again. Dismissal calls finishPass().
    if (lastAttemptSpent) {
      return;
    }
    passTimeoutRef.current = setTimeout(() => {
      passTimeoutRef.current = null;
      setIsHoldingPassResult(false);
      setIsExpanded(false);
    }, PRESENTATION_PRONUNCIATION_PASS_DISMISS_MS);
  };

  // Out of attempts, so staying on free also closes the practice rather than leaving it usable.
  const handleUpsellContinue = () => {
    setUpsellTrigger(null);
    if (hasPassedRef.current) {
      finishPass();
      return;
    }
    setIsExpanded(false);
  };

  const upsellModal = upsellTrigger && (
    <PronunciationTokenUpsell
      onContinue={handleUpsellContinue}
      legacy={
        <PronunciationLimitUpsell
          source="say_it_yourself_no_tokens"
          trigger={upsellTrigger}
          banner={intl.formatMessage(msgs.dailyLimitBanner, {
            count: PRESENTATION_PRONUNCIATION_DAILY_FREE_TOTAL,
          })}
          onContinue={handleUpsellContinue}
        />
      }
    />
  );

  if (hasPassedLearnable && !isHoldingPassResult) {
    return upsellTrigger ? upsellModal : null;
  }

  const pillPrompt = (
    <S.PillPrompt
      type="button"
      data-testid="presentationPronunciationPracticePrompt"
      onClick={handleExpand}
    >
      <S.PillMicIcon>
        <S.PronunciationIcon name="microphoneOutline" />
      </S.PillMicIcon>
      <S.PillText>
        <S.PillTitle>{intl.formatMessage(msgs.promptTitle)}</S.PillTitle>
        {attemptsLeftLabel && (
          <S.PillAttemptsLeft data-testid="presentationPronunciationAttemptsLeft">
            {attemptsLeftLabel}
          </S.PillAttemptsLeft>
        )}
      </S.PillText>
    </S.PillPrompt>
  );

  // The permissions copy is a tall screen of its own. In the blob column it
  // overflows the lesson and clips the phrase and continue button, so it opens
  // as a dialog instead of expanding inline.
  if (pill && isExpanded && micPermissionState !== 'granted') {
    return (
      <>
        {pillPrompt}
        <S.PermissionModal
          isOpen
          handleClose={handleCollapse}
          darkCloseIcon
          shouldCloseOnEsc
          contentLabel={intl.formatMessage(msgs.micPermissionTitle)}
        >
          <S.PermissionModalBody data-testid="mic-permission-dialog">
            <MicPermissionsView
              isDenied={micPermissionState === 'denied'}
              onClick={() => {
                void handleGiveMicPermissions();
              }}
              compact
            />
            {attemptsLeftLabel && <S.AttemptsFooter>{attemptsLeftLabel}</S.AttemptsFooter>}
          </S.PermissionModalBody>
        </S.PermissionModal>
        {upsellModal}
      </>
    );
  }

  if (!isExpanded && pill) {
    return (
      <>
        {pillPrompt}
        {upsellModal}
      </>
    );
  }

  if (!isExpanded) {
    return (
      <>
        <S.Prompt
          data-testid="presentationPronunciationPracticePrompt"
          $overlay={overlay}
          onClick={handleExpand}
        >
          <S.MicIconWrapper $overlay={overlay}>
            <S.PronunciationIcon name="microphoneOutline" />
          </S.MicIconWrapper>
          <S.PromptText $overlay={overlay}>
            <S.PromptTitle $overlay={overlay}>{intl.formatMessage(msgs.promptTitle)}</S.PromptTitle>
            {attemptsLeftLabel && (
              <S.AttemptsLeft
                data-testid="presentationPronunciationAttemptsLeft"
                $overlay={overlay}
              >
                {attemptsLeftLabel}
              </S.AttemptsLeft>
            )}
            {!overlay && !isRationed && (
              <S.PromptDescription>
                {intl.formatMessage(msgs.promptDescription)}
              </S.PromptDescription>
            )}
          </S.PromptText>
          {!overlay && <S.ChevronIcon name="chevronRight" />}
        </S.Prompt>
        {upsellModal}
      </>
    );
  }

  return (
    <S.ExpandedContainer data-testid="presentationPronunciationPractice" $overlay={overlay}>
      <S.CloseButton
        type="button"
        onClick={handleCollapse}
        aria-label={intl.formatMessage(msgs.close)}
        data-testid="presentationPronunciationPracticeClose"
      >
        <S.CloseIcon name="close" />
      </S.CloseButton>
      <LearnablePronunciation
        key={sessionId}
        learnable={learnable}
        sessionId={sessionId}
        mode={PronunciationMode.LEARN_SESSION}
        compact
        hideVideoButton={hasVideo}
        continueButtons={null}
        onCorrectPronunciation={handleCorrectPronunciation}
      />
      {attemptsLeftLabel && (
        <S.AttemptsFooter data-testid="presentationPronunciationAttemptsLeft" $overlay={overlay}>
          {attemptsLeftLabel}
        </S.AttemptsFooter>
      )}
      {upsellModal}
    </S.ExpandedContainer>
  );
};
